<script setup lang="ts">
import { ref, reactive } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useFormValidation } from '@/composables/useFormValidation'
import AuthLayout from '@/components/layout/AuthLayout.vue'
import MockDataButton from '@/components/dev/MockDataButton.vue'
import { serializeDate } from '@/lib/utils/date'
import {
  validateEmail,
  validateDateOfBirth,
  validateRequired,
} from '@/lib/validation'
import { faker } from '@faker-js/faker'

const router = useRouter()
const { register } = useAuth()

// Form data using reactive
const formData = reactive({
  email: '',
  date: '',
  maritalStatus: 'single',
  pensionPlanId: '',
})

const error = ref('')
const apiErrors = ref<Record<string, string[]>>({})

const maritalStatusOptions = [
  { label: 'Single', value: 'single' },
  { label: 'Married', value: 'married' },
]

// Validation schema
const validationSchema = {
  email: validateEmail,
  date: validateDateOfBirth,
  pensionPlanId: validateRequired,
}

const {
  submitting,
  errors,
  isValid,
  handleFieldTouch,
  markAllFieldsTouched,
  getInputClass,
  setSubmitting,
} = useFormValidation(validationSchema, formData)

// Custom function to get field error message (from validation or API)
const getFieldError = (field: string) => {
  if (apiErrors.value[field]?.length) {
    return apiErrors.value[field][0]
  }
  return errors.value[field as keyof typeof errors.value]
}

const handleRegister = async () => {
  markAllFieldsTouched()
  if (!isValid.value) return

  setSubmitting(true)
  error.value = ''
  apiErrors.value = {}

  try {
    const dob = serializeDate(formData.date)

    if (!dob) {
      error.value = 'Invalid date of birth'
      return
    }

    const registerData = {
      email: formData.email.trim(),
      details: {
        dob,
        maritalStatus: formData.maritalStatus,
        pensionPlanId: formData.pensionPlanId.trim(),
      },
    }

    await register(registerData)
    router.push('/pension-info')
  } catch (e: any) {
    // Handle API validation errors
    if (e.response?.data?.errors) {
      apiErrors.value = e.response.data.errors

      // If there's a general message, display it
      if (e.response.data.message) {
        error.value = e.response.data.message
      }
    } else {
      error.value =
        'Something went wrong. Please try again. (Error: ' +
        ((e as Error).message ?? e) +
        ')'
    }
    console.error(e)
  } finally {
    setSubmitting(false)
  }
}

const handleDateChange = (value: string) => {
  let numericOnly = value.replace(/[^0-9]/g, '')
  if (numericOnly.length > 2) {
    numericOnly = `${numericOnly.slice(0, 2)}.${numericOnly.slice(2)}`
  }
  if (numericOnly.length > 5) {
    numericOnly = `${numericOnly.slice(0, 5)}.${numericOnly.slice(5, 9)}`
  }
  formData.date = numericOnly
}

const fillMockData = () => {
  formData.maritalStatus = faker.helpers.arrayElement(['single', 'married'])
  formData.pensionPlanId = faker.finance.accountNumber(8)
  formData.date = faker.date
    .between({
      from: new Date('1950-01-01'),
      to: new Date('2005-12-31'),
    })
    .toLocaleDateString('en-IE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '.')
  formData.email = faker.internet.email()
}
</script>

<template>
  <AuthLayout title="Account details">
    <div class="max-w-md mx-auto space-y-6">
      <div class="flex flex-col items-center mb-8">
        <img
          src="/images/create-account-avatar.png"
          alt="Welcome back"
          class="w-48 h-48 object-contain mb-4"
        />
        <h1 class="text-3xl font-bold text-center">Create your account</h1>
        <p class="mt-4">
          Your information is secure with Maximo. We use the latest in
          encryption and will not share information with any providers without
          your permission.
        </p>
      </div>

      <form @submit.prevent="handleRegister" class="space-y-4">
        <div class="space-y-2">
          <Select v-model="formData.maritalStatus">
            <SelectTrigger>
              <SelectValue placeholder="Marital status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="option in maritalStatusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>

          <div class="space-y-1">
            <Input
              placeholder="Enter date of birth (DD.MM.YYYY)"
              v-model="formData.date"
              @input="handleDateChange($event.target.value)"
              :class="getInputClass('date')"
              @blur="handleFieldTouch('date')"
            />
            <p v-if="getFieldError('date')" class="text-sm text-red-500">
              {{ getFieldError('date') }}
            </p>
          </div>

          <div class="space-y-1">
            <Input
              type="email"
              placeholder="Your email address"
              v-model="formData.email"
              :class="getInputClass('email')"
              @blur="handleFieldTouch('email')"
            />
            <p v-if="getFieldError('email')" class="text-sm text-red-500">
              {{ getFieldError('email') }}
            </p>
          </div>

          <div class="space-y-1">
            <Input
              placeholder="Irish Life Pension Plan ID *"
              v-model="formData.pensionPlanId"
              :class="getInputClass('pensionPlanId')"
              @blur="handleFieldTouch('pensionPlanId')"
            />
            <p
              v-if="getFieldError('pensionPlanId')"
              class="text-sm text-red-500"
            >
              {{ getFieldError('pensionPlanId') }}
            </p>
          </div>
        </div>

        <Button
          type="submit"
          size="lg"
          class="w-full"
          :disabled="submitting || !isValid"
        >
          Register
        </Button>
      </form>

      <div class="text-center">
        <p class="text-sm text-gray-600">
          Already have an account?
          <Button
            variant="link"
            class="px-1"
            @click="router.push('/auth/login')"
          >
            Login here
          </Button>
        </p>
      </div>
    </div>

    <MockDataButton :onClick="fillMockData" />
  </AuthLayout>
</template>
